<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from "vue";
import {
  requestNotificationPermission,
  onMessageListener,
} from "./utils/firebase";
import { useAuthStore } from "./stores/auth";
import { storeToRefs } from "pinia";
import { useScreenSize } from "./utils/useScreenSize";
import MobileNavTabs from "./components/layout/navigation/MobileNavTabs.vue";
import Header from "./components/layout/Header.vue";
import AuthFooter from "./components/layout/app/AuthFooter.vue";
import Toast from "./services/Toast";
import LoadingOverlay from "./components/ui/common/LoadingOverlay.vue";
import NotificationContainer from "./components/ui/notification/NotificationContainer.vue";
import { useLayout } from "./composables/useLayout";
import Sidebar from "./components/layout/app/Sidebar.vue";
import { tokenManager } from "./api/utils/tokenManager";
import { api } from "./api/Api";
import { User } from "./types/auth";
const store = useAuthStore();
const { isMobile } = useScreenSize();
const { isAuthenticated } = storeToRefs(store);
const { isMobileMenuOpen, toggleMobileMenu } = useLayout();
//const { initTour } = useAppTour()

/*const handleCompanySelect = (company : Company) => {
  store.selectCompany(company)
  initTour()
}*/

const isScrolledDown = ref<boolean>(false);
const handleScroll = () => {
  // Check if the user has scrolled down more than 50px
  if (window.scrollY > 50) {
    isScrolledDown.value = true;
  } else {
    isScrolledDown.value = false;
  }
};

const isWelcome = ref(false);
const isMinimized = ref(false);
const toggleSidebar = (welcome: boolean, minimized: boolean) => {
  isWelcome.value = welcome;
  isMinimized.value = minimized;
};

onBeforeUnmount(() => {
  // Cleanup event listeners
  window.removeEventListener("scroll", handleScroll);
});


const updateToken = async (token: string) => {
  const width = window.innerWidth;
  let deviceType = 'unknown';
    if (width <= 768) {
      deviceType = 'mobile';
    } else if (width > 768 && width <= 1024) {
      deviceType = 'tablet';
    } else {
      deviceType = 'desktop';
    }
    
    api.post("notifications/token", { 
      token: token,
      device_type: deviceType,
      platform: navigator.platform,
      user_agent: navigator.userAgent,
      id: tokenManager.getUser()?.uid
     }, {});
};

const user = ref<User>();

onMounted(async () => {
  // Check current permission status
  user.value = tokenManager.getUser() ?? undefined;
  if (tokenManager.getUser()) {
    const currentPermission = Notification.permission;
    if (currentPermission === "granted") {
      const token = await requestNotificationPermission();
      /*console.log(navigator.userAgent)
      console.log(navigator.platform)
      console.log(navigator.onLine)
      console.log(navigator.userAgent)
      console.log(navigator.userAgent)
      console.log(navigator.userAgent)*/
      if (token) {
        updateToken(token);
      }
    } else if (currentPermission === "denied") {
      Toast.show(
        "Notifications are blocked. Please enable them in your browser settings.",
        "error"
      );
    }

    onMessageListener().then((payload: any) => {
      //console.log("Message received:", payload);
      if ("Notification" in window && Notification.permission === "granted") {
        new Notification(payload.notification.title, {
          body: payload.notification.body,
          icon: payload.notification.icon,
        });
      }
    });
  }

  // Add scroll event listener
  window.addEventListener("scroll", handleScroll);

  // Check for mobile screen on mount

  // For Android (Chrome)
  if (navigator.userAgent.match(/Android/i)) {
    setTimeout(() => {
      window.scrollTo(0, 1); // Scroll down by 1px to hide address bar
    }, 100);
  }
  /*setTimeout(() => {
    notificationStore.addNotification({
      title: 'Welcome Back!',
      message: 'Thank you for using our application. Need help? Click here to view our documentation.',
      type: 'info',
      action: {
        label: 'View Documentation',
        onClick: () => {
          window.location.href = '/documentation'
        }
      }
    })
  }, 3000)*/ // 60000 ms = 1 minute
});
</script>

<template>
  <div class="min-h-screen bg-gray-50 dark:bg-black">
    <LoadingOverlay />
    <NotificationContainer />
    <template v-if="isAuthenticated">
      <Header
        id="app-header"
        v-show="!isScrolledDown || !isMobile"
        @toggle-mobile-menu="toggleMobileMenu"
      />
      <Sidebar
        id="app-sidebar"
        @toggle="toggleSidebar"
        v-if="!isWelcome && user?.user_type == 'Back Office'"
        v-model:isMobileOpen="isMobileMenuOpen"
      />
      <main
        class="pt-24 md:pb-16 pb-[100px]"
        :class="{
          'md:ml-64': !isWelcome && !isMinimized,
          'md:ml-[70px]': isMinimized && !isWelcome,
          'mr-[20px]': isWelcome,
        }"
      >
        <div
          class="max-auto p-0"
          :class="[isWelcome ? 'md:ml-2 md:-mr-2' : 'mr-4 ml-2']"
        >
          <RouterView />
          <iframe
            ref="pdfFrame"
            id="pdf-printer"
            style="display: none"
          ></iframe>
        </div>
      </main>

      <MobileNavTabs v-if="isMobile && user?.user_type != 'Delivery' && user?.user_type != 'POS User'" />
      <AuthFooter class="hidden md:flex" />
    </template>

    <!-- Show home page when not authenticated -->
    <template v-else>
      <RouterView />
    </template>
  </div>
</template>
